import { ActionIcon, Avatar, Menu, Stack, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDots, IconTrash, IconUserMinus, IconUserPlus } from '@tabler/icons-react';
import { api } from '@/clients/admin.client';
import { notifyError, notifySuccess } from '@/lib/notification';
import { logger } from '@/lib/logger';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { ProjectMember } from '@/clients/types';
import { UserInfo } from '@/lib/auth';

type Props = {
  adminClient: typeof api;
  members: ProjectMember[];
  projectId: string;
  readonly: boolean;
  userInfo: UserInfo;
};

export function ProjectMemberList({ adminClient, members, projectId, readonly, userInfo }: Props) {
  const router = useRouter();
  const navigate = useNavigate();
  const handleRemoveUser = async (user: ProjectMember) => {
    try {
      await adminClient.settings_v1_remove_project_member(undefined, {
        params: {
          project_id: projectId,
          user_id: user.user_id,
        },
      });
      notifySuccess(`ユーザ ${user.email} をプロジェクトメンバーから外しました。`);
      if (userInfo.userId === user.user_id) {
        navigate({ to: '/' });
        return;
      }
      await router.load();
    } catch (error) {
      logger.error(error);
      notifyError(`ユーザ ${user.email} をプロジェクトメンバーから外すことに失敗しました。`);
    }
  };

  const handleAssignOwnerRole = async (user: ProjectMember) => {
    try {
      await adminClient.settings_v1_update_project_member_role(
        {
          role: 'owner',
        },
        {
          params: {
            project_id: projectId,
            user_id: user.user_id,
          },
        }
      );
      await router.load();
      notifySuccess(`ユーザ ${user.email} のロールを変更しました。`);
    } catch (error) {
      logger.error(error);
      notifyError(`ユーザ ${user.email} の変更に失敗しました。`);
    }
  };

  const handleRevokeOwnerRole = async (user: ProjectMember) => {
    try {
      await adminClient.settings_v1_update_project_member_role(
        {
          role: 'member',
        },
        {
          params: {
            project_id: projectId,
            user_id: user.user_id,
          },
        }
      );
      await router.load();
      notifySuccess(`ユーザ ${user.email} のロールを変更しました。`);
    } catch (error) {
      logger.error(error);
      notifyError(`ユーザ ${user.email} の変更に失敗しました。`);
    }
  };

  const openConfirmRemoveUserModal = (user: ProjectMember) => {
    modals.openConfirmModal({
      title: 'ユーザをプロジェクトメンバーから外しますか？',
      children: (
        <Stack>
          <Text size="sm">
            この操作により、 <strong>{user.email}</strong> はプロジェクトメンバーから外されます。
          </Text>
          <Text size="xs">※ テナント管理者の場合は、引き続きプロジェクトにアクセスできます。</Text>
        </Stack>
      ),
      onConfirm: () => handleRemoveUser(user),
    });
  };

  return (
    <Table.ScrollContainer minWidth={700} className="plamo-stack-sm">
      <Table horizontalSpacing="lg">
        <Table.Thead>
          <Table.Tr>
            <Table.Th />
            <Table.Th>メールアドレス</Table.Th>
            <Table.Th>名前</Table.Th>
            <Table.Th>ロール</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {members.map((user, i) => (
            <Table.Tr key={i}>
              <Table.Td data-stack-hidden>
                {(user.picture && <Avatar src={user.picture} alt={user.name} />) || (
                  <Avatar radius="xl" />
                )}
              </Table.Td>
              <Table.Td data-label="メールアドレス">{user.email}</Table.Td>
              <Table.Td data-label="名前">{user.name}</Table.Td>
              <Table.Td data-label="ロール">
                {user.project_role === 'owner' ? 'プロジェクト管理者' : 'メンバー'}
              </Table.Td>
              <Table.Td data-action>
                {!readonly && (
                  <Menu>
                    <Menu.Target>
                      <ActionIcon variant="outline" size="md">
                        <IconDots style={{ width: '70%', height: '70%' }} stroke={1.5} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {user.project_role === 'owner' ? (
                        <Menu.Item
                          leftSection={<IconUserMinus style={{ width: '70%', height: '70%' }} />}
                          onClick={() => {
                            handleRevokeOwnerRole(user);
                          }}
                        >
                          メンバーに変更
                          <Text size="xs" c="gray">
                            このプロジェクトの情報の閲覧だけが
                            <br />
                            できます。
                          </Text>
                        </Menu.Item>
                      ) : (
                        <Menu.Item
                          leftSection={<IconUserPlus style={{ width: '70%', height: '70%' }} />}
                          onClick={() => {
                            handleAssignOwnerRole(user);
                          }}
                        >
                          プロジェクト管理者に変更
                          <Text size="xs" c="gray">
                            このプロジェクトの API キー管理と
                            <br />
                            メンバーの追加・削除ができます。
                          </Text>
                        </Menu.Item>
                      )}

                      <Menu.Item
                        leftSection={
                          <IconTrash
                            style={{ width: '70%', height: '70%' }}
                            className="text-plamo-error"
                          />
                        }
                        onClick={() => {
                          openConfirmRemoveUserModal(user);
                        }}
                      >
                        <div className="text-plamo-error">プロジェクトから外す</div>
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
